/**
 * Work order service lines: building a line from a priced service, and choosing services from a
 * service group (Service Catalog → Service Groups). Prices come from the services already resolved
 * for the selected client, so a group never bypasses client-specific pricing.
 */

const round2 = (n) => Number(Number(n).toFixed(2));
let seq = 0;

// What one unit of the service costs the customer: service charge + its VAT + government fee
export const priceOf = (service) =>
  round2((service.service_charge || 0) + (service.tax_on_service_charge || 0) + (service.govt_charge || 0));

/**
 * A line for the services table. `markupInclVat` is what the user typed (VAT included) and is split
 * with the same rounding the invoice uses. `group` ({id, name}) tags lines added from a service group.
 */
export const buildServiceLine = (service, markupInclVat = 0, group = null) => {
  const serviceCharge = service.service_charge || 0;
  const serviceTax = service.tax_on_service_charge || 0;
  const govtCharge = service.govt_charge || 0;
  const markupIncl = parseFloat(markupInclVat) || 0;
  const markupBase = round2(markupIncl / 1.05);
  const markupVat = round2(markupIncl - markupBase);
  seq += 1;
  return {
    id: `temp-${Date.now()}-${seq}`,
    service_id: service.id,
    service_name: service.name,
    service_description: service.description || '',
    quantity: 1,
    unit_price: serviceCharge + govtCharge,
    service_charge: serviceCharge,
    govt_charge: govtCharge,
    tax_value: serviceTax,
    tax_on_markup: markupVat,
    line_total: round2(serviceCharge + govtCharge + markupBase + serviceTax + markupVat),
    original_line_total: round2(serviceCharge + govtCharge + serviceTax),
    markup_amount: markupBase,
    is_custom_service: service.type === 'INTERNAL',
    service_type: service.type || 'THAKLIS',
    fine_amount: 0,
    creates_task: service.createsTask || false,
    documentRequirements: service.documentRequirements || service.document_requirements || [],
    customFields: (service.customFields || service.custom_fields || []).map((f) => ({
      fieldId: f.fieldId || f.field_id || f.code,
      code: f.code,
      label: f.label,
      fieldType: f.fieldType || f.field_type || 'TEXT',
      required: f.required,
      options: f.options || [],
      // Default value the selected org set for this field (backend pre-fills it into `value`).
      orgDefault: f.orgDefault ?? f.value ?? '',
    })),
    ...(group ? { group_id: group.id, group_name: group.name } : {}),
  };
};

/**
 * The group's services in their set order, each matched to this client's priced service.
 * `available` is false when the client has no price for it (or it is no longer active).
 */
export const groupChoices = (group, availableServices) => {
  const byId = new Map((availableServices || []).filter((s) => s.type === 'INTERNAL').map((s) => [String(s.id), s]));
  return [...(group?.items || [])]
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .map((item) => {
      const service = byId.get(String(item.serviceId)) || null;
      return {
        serviceId: String(item.serviceId),
        name: service?.name || item.serviceName || 'Service',
        notes: item.notes || '',
        required: item.required !== false,
        available: !!service,
        service,
      };
    });
};

// Required services this client can take start ticked; optional ones start unticked
export const defaultPicked = (choices) =>
  new Set(choices.filter((c) => c.available && c.required).map((c) => c.serviceId));
